<template>
    <div
        class="d-flex mb-0 title__wrapper align-center"
        :style="pageHeader ? { '--page-header-parent-gap': pageHeaderGap + 'px' } : null"
        :class="[
            className,
            { 'title__wrapper--with-details': $slots.details, 'title__wrapper--page': pageHeader, 'title__wrapper--scrolled': scrolled },
        ]"
    >
        <div
            class="d-flex flex-grow-1 align-center title__lead"
            :class="{ title__main: $slots.details }"
        >
            <div class="title">
                <div class="d-flex align-center">
                    <v-btn
                        @click="$router.back()"
                        text
                        small
                        class="px-0 b mr-5 white back__button mr-2 d-none d-sm-block"
                        cusor="pointer"
                        v-if="back"
                    >
                        <v-icon color="primary">{{ iconName("back") }}</v-icon>
                    </v-btn>
                    <div class="d-flex align-center title__heading">
                        <v-icon v-if="icon" color="primary" :size="headingHeight" class="title__icon">
                            {{ iconName(icon) || icon }}
                        </v-icon>
                        <div class="title__heading-body">
                            <div ref="headingText">
                            <div class="d-flex align-baseline title__heading-row">
                            <div class="title__text mr-2">
                                <span class="title__name">
                                    <MiddleEllipsisText :text="text" />
                                </span>
                                <template v-if="normalizedTags.length">
                                    <template v-for="tag in normalizedTags">
                                        <v-tooltip
                                            v-if="tag.tooltip"
                                            bottom
                                            :key="`${tag.key}-tooltip`"
                                        >
                                            <template v-slot:activator="{ on, attrs }">
                                                <v-chip
                                                    :ripple="false"
                                                    v-bind="attrs"
                                                    v-on="tag.clickable ? { ...on, click: () => $emit('tag-click', tag) } : on"
                                                    :color="tag.color"
                                                    :class="tag.class"
                                                    :clickable="tag.clickable"
                                                >
                                                        <v-icon
                                                            v-if="tag.icon"
                                                            small
                                                            :color="tag.iconColor"
                                                            :left="!!tag.text"
                                                            class="tag-chip__icon"
                                                        >
                                                            {{ tag.icon }}
                                                        </v-icon>
                                                        <span v-if="tag.text" class="tag-chip__text">{{ tag.text }}</span>
                                                </v-chip>
                                            </template>
                                            <span>{{ tag.tooltip }}</span>
                                        </v-tooltip>
                                        <v-chip
                                            v-else
                                                :ripple="false"
                                            :key="tag.key"
                                            :color="tag.color"
                                            :class="tag.class"
                                            :clickable="tag.clickable"
                                            @click="tag.clickable ? $emit('tag-click', tag) : null"
                                        >
                                                <v-icon
                                                    v-if="tag.icon"
                                                    small
                                                    :color="tag.iconColor"
                                                    :left="!!tag.text"
                                                    class="tag-chip__icon"
                                                >
                                                    {{ tag.icon }}
                                                </v-icon>
                                                <span v-if="tag.text" class="tag-chip__text">{{ tag.text }}</span>
                                        </v-chip>
                                    </template></template
                                >
                            </div>

                            </div>
                            <p v-if="subtitle" class="title__subtitle">{{ subtitle }}</p>
                            </div>
                            <div v-if="$slots.details" class="title__details">
                                <slot name="details" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="title__actions-bar" v-if="reserveActions || primaryAction || secondaryGroups.length">
            <div v-if="primaryAction || secondaryGroups.length" class="title__split-action">
                <DisabledTooltipActivator v-if="primaryAction" :tooltip="primaryAction.tooltip" wrapper-class="title__primary-wrap">
                    <v-btn
                        class="title__action-btn" color="primary" elevation="0"
                        :class="{ 'title__action-btn--split': secondaryGroups.length }"
                        :loading="primaryAction.loading"
                        :disabled="!actionsEnabled || primaryAction.disabled"
                        :title="primaryAction.label"
                        @click.stop="actionClicked(primaryAction)"
                    >
                        <v-icon v-if="primaryAction.icon" color="white" small left>{{ iconName(primaryAction.icon) || primaryAction.icon }}</v-icon>
                        <span class="title__action-label">{{ primaryAction.label }}</span>
                    </v-btn>
                </DisabledTooltipActivator>
                <v-menu v-if="secondaryGroups.length" offset-y left content-class="title__action-menu-content compact-action-menu">
                    <template #activator="{ on, attrs }">
                        <v-btn class="title__action-toggle" :class="{ 'title__action-toggle--only': !primaryAction }" color="primary" elevation="0"
                            :disabled="!actionsEnabled" :aria-label="$translate('otherActions')" v-bind="attrs" v-on="on">
                            <v-icon v-if="!primaryAction" color="white" small left>mdi-menu</v-icon>
                            <span v-if="!primaryAction" class="title__action-label">{{ $translate('actionsMenu') }}</span>
                            <v-icon color="white" :right="!primaryAction">mdi-chevron-down</v-icon>
                        </v-btn>
                    </template>
                    <v-list class="title__action-menu">
                        <template v-for="(group, groupIndex) in secondaryGroups">
                            <v-divider v-if="groupIndex" :key="`divider-${groupIndex}`" />
                            <v-list-item v-for="(item, itemIndex) in group" :key="`${groupIndex}-${itemIndex}`"
                                :disabled="isSubmenuItemDisabled(item)" :title="item.tooltip"
                                @click="actionClicked(item)">
                                <v-list-item-icon v-if="item.icon" class="mr-2 align-self-center">
                                    <v-icon small :color="item.color">{{ iconName(item.icon) || item.icon }}</v-icon>
                                </v-list-item-icon>
                                <v-list-item-content>
                                    <v-list-item-title>{{ item.label }}</v-list-item-title>
                                    <v-list-item-subtitle v-if="item.tooltip">{{ item.tooltip }}</v-list-item-subtitle>
                                </v-list-item-content>
                                <v-progress-circular v-if="item.loading" indeterminate size="16" width="2" />
                            </v-list-item>
                        </template>
                    </v-list>
                </v-menu>
            </div>
        </div>
    </div>
</template>

<script>
import { titleActions } from "@/utils/title-actions";
import GlobalMixins from "@/mixins/global";
import DisabledTooltipActivator from "@/components/common/disabled-tooltip-activator.vue";
import MiddleEllipsisText from "@/components/common/middle-ellipsis-text.vue";

export default {
    components: { DisabledTooltipActivator, MiddleEllipsisText },
    data: () => ({ pageHeader: false, scrolled: false, pageHeaderGap: 0, headingHeight: 27 }),
    mounted() {
        const updateHeadingHeight = () => {
            this.headingHeight = this.$refs.headingText.getBoundingClientRect().height;
        };
        updateHeadingHeight();
        this.headingSizeObserver = new ResizeObserver(updateHeadingHeight);
        this.headingSizeObserver.observe(this.$refs.headingText);
        const page = this.$el.closest('.connected-page-content');
        this.pageHeader = !!page && page.querySelector('.title__wrapper') === this.$el && !this.$el.closest('.v-dialog');
        if (!this.pageHeader) return;
        // Page stacks own their section gap; compensate it only below this header.
        this.updateHeaderGap = () => {
            this.pageHeaderGap = parseFloat(getComputedStyle(this.$el.parentElement).rowGap) || 0;
        };
        this.updateHeaderGap();
        this.headerLayoutObserver = new ResizeObserver(this.updateHeaderGap);
        this.headerLayoutObserver.observe(this.$el.parentElement);
        this.scrollContainer = page.closest('.v-main__wrap');
        this.updateHeaderScroll = () => { this.scrolled = this.scrollContainer.scrollTop > 4; };
        this.scrollContainer?.addEventListener('scroll', this.updateHeaderScroll, { passive: true });
        this.updateHeaderScroll();
    },
    beforeDestroy() {
        this.scrollContainer?.removeEventListener('scroll', this.updateHeaderScroll);
        this.headerLayoutObserver?.disconnect();
        this.headingSizeObserver?.disconnect();
    },
    mixins: [GlobalMixins],
    props: {
        className: {
            type: String,
            required: false,
        },
        back: {
            type: Boolean,
            default: true,
        },
        text: {
            type: String,
            required: true,
        },
        subtitle: {
            type: String,
            required: false,
        },
        subtitleBelow: {
            type: Boolean,
            default: false,
        },
        icon: {
            type: String,
            required: false,
        },
        actions: {
            type: Array,
            required: false,
        },
        reserveActions: {
            type: Boolean,
            default: false,
        },
        actionsEnabled: {
            type: Boolean,
            default: true,
        },
        edit: {
            type: Boolean,
            required: false,
        },
        tags: {
            type: Array,
            required: false,
            default: () => [],
        },
        helpContentId: {
            type: String,
            required: false,
        },
    },
    computed: {
        actionPresentation() {
            return titleActions(this.actions, this.$store?.state?.session?.active_locale || this.$i18n?.locale || 'fr');
        },
        primaryAction() { return this.actionPresentation.primary; },
        secondaryGroups() { return this.actionPresentation.groups; },
        normalizedTags() {
            const defaultColor = "primary-dark";
            const defaultClasses = ["justify-center"];

            return (this.tags || []).map((tag, index) => {
                let text;
                let icon;
                let iconColor;
                let color = defaultColor;
                let textColor;
                let classes = [...defaultClasses];
                let key;
                let tooltip;

                if (typeof tag === "string") {
                    text = tag;
                    key = `tag-${index}-${text}`;
                } else if (tag && typeof tag === "object") {
                    text = tag.text ?? tag.label ?? tag.value ?? null;
                    icon = tag.icon ?? tag.iconName ?? tag.icon_name ?? null;
                    iconColor = tag.iconColor ?? tag.icon_color ?? tag.iconColour ?? tag.icon_colour ?? textColor;
                    color = tag.color ?? defaultColor;
                    textColor = tag.textColor ?? tag.text_color ?? tag.textColour ?? tag.textcolour;
                    tooltip = tag.tooltip ?? tag.title ?? tag.help ?? null;
                    if (tag.classes) {
                        if (Array.isArray(tag.classes)) {
                            classes = [...tag.classes];
                        } else if (typeof tag.classes === "string") {
                            classes = tag.classes.split(/\s+/).filter(Boolean);
                        }
                    }
                    key = tag.key ?? `tag-${index}-${text}`;
                    if (!text && !icon) {
                        text = "";
                    }
                } else {
                    text = String(tag);
                    key = `tag-${index}-${text}`;
                }

                const classSet = new Set(["ml-2", ...classes.filter(Boolean)]);
                classSet.add(textColor || "white--text");
                classSet.add(tag.text ? "tag-chip--with-text" : "tag-chip--icon-only");

                return {
                    key,
                    text,
                    icon,
                    iconColor,
                    color,
                    class: Array.from(classSet).join(" ") || "ml-2",
                    tooltip,
                    clickable: tag.clickable ?? false,
                };
            });
        },
    },
    i18n: { messages: { fr: { otherActions: "Autres actions", actionsMenu: "Actions" }, en: { otherActions: "Other actions", actionsMenu: "Actions" } } },
    methods: {
        isSubmenuItemDisabled(item) {
            return !this.actionsEnabled || !!item.disabled || !!item.loading;
        },
        actionClicked(action) {
            if (!this.actionsEnabled || action.disabled || action.loading) {
                return;
            }
            if (action.link) {
                this.routerPush(action.link);
            } else {
                this.$emit(action.action, action.value);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.title__text {
    font-size: 32px;
    font-weight: 700;
    line-height: 1.25;
    letter-spacing: -0.7px;
    color: #25203f;
    font-family: "Inter", sans-serif;

    @media (max-width: 700px) {
        font-size: 26px;
    }
}

.title__icon {
    align-self: flex-start;
    flex-shrink: 0;
    margin-right: 10px;
}

.title__wrapper .title__subtitle {
    margin: 0;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.3;
    letter-spacing: normal;
    color: #746d84;
}

.title__wrapper .title__subtitle--inline {
    flex: 0 1 auto;
    margin: 0 12px 0 4px;
    font-size: 14px;
}

.tag-chip__icon {
    font-size: 17px !important;
    margin-left: 0 !important;
    margin-right: 4px;
}

.tag-chip--icon-only {
    justify-content: center;
    .tag-chip__icon {
        margin-right: 0;
    }
}

.tag-chip__text + .tag-chip__icon {
    margin-left: 4px;
}

.title__action-menu-item--disabled {
    opacity: 0.45;
}

.title__wrapper {
    position: relative;

    .title__lead {
        min-width: 0;
    }

    .title__main {
        min-width: 0;
        flex: 1 1 auto;
    }

    .title,
    .title > .d-flex {
        min-width: 0;
        max-width: 100%;
        width: 100%;
    }

    .title__heading,
    .title__heading-body,
    .title__heading-row {
        min-width: 0;
        max-width: 100%;
    }

    .title__heading,
    .title__heading-body {
        flex: 1 1 auto;
    }

    .title__heading-row {
        width: 100%;
    }

    &--with-details {
        @media (min-width: 600px) {
            flex-wrap: nowrap !important;
        }
        .title__main {
            overflow: hidden;
        }
        .title__actions {
            flex: 0 0 auto;
        }
        .title__details {
            margin-top: 0;
            line-height: 1.3;
            min-width: 0;
            max-width: 100%;
            overflow: hidden;
        }
        .title__details-line {
            min-width: 0;
            max-width: 100%;
            overflow: hidden;
        }
        .title__details-description {
            flex: 1 1 auto;
            min-width: 0;
            max-width: 100%;
            overflow: hidden;
            display: block;
            white-space: nowrap;
        }
        .title__details ::v-deep .title__details-description,
        .title__details ::v-deep .v-tooltip,
        .title__details ::v-deep .middle-ellipsis-text {
            display: block;
            min-width: 0;
            max-width: 100%;
            overflow: hidden;
            white-space: nowrap;
        }
    }
    .title__text {
        display: flex;
        align-items: center;
        flex: 1 1 auto;
        flex-wrap: nowrap;
        min-width: 0;
        max-width: 100%;
        white-space: nowrap;
        overflow: hidden;
    }

    .title__text ::v-deep .v-chip {
        flex-shrink: 0;
    }

    .title__name {
        flex: 0 1 auto;
        min-width: 0;
        max-width: 100%;
        overflow: hidden;
        display: block;

        ::v-deep .v-tooltip,
        ::v-deep .middle-ellipsis-text {
            display: block;
            min-width: 0;
            max-width: 100%;
            overflow: hidden;
        }
    }
    .text-h4 {
        white-space: nowrap;
        overflow: hidden;
    }
    .back__button {
        height: 40px;
        width: 40px;
    }
}


.title__action-menu {
    padding-top: 4px;
    padding-bottom: 4px;

    ::v-deep .v-list-item {
        min-height: 32px;
    }

    ::v-deep .v-list-item__icon {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
        min-width: auto;
    }

    ::v-deep .v-list-item__content {
        flex: 0 1 auto;
        min-width: 0;
        padding-top: 6px;
        padding-bottom: 6px;
    }

    ::v-deep .v-list-item__title {
        white-space: nowrap;
    }
}

.title__wrapper {
    container: page-title / inline-size;
    flex-wrap: wrap !important;
    gap: 16px;
    .title__lead { flex: 999 1 360px !important; overflow: hidden; }
    .title__subtitle { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .title__actions-bar { display: flex; flex: 1 0 auto; min-width: 0; max-width: 100%; margin-left: auto; align-items: center; flex-wrap: nowrap; }
}
.title__split-action { display: flex; width: 100%; min-width: 0; max-width: 100%; align-items: center; }
.title__split-action ::v-deep .title__primary-wrap { min-width: 0; flex: 1 1 auto; }
.title__action-btn { width: 100%; min-width: 0 !important; max-width: 100%; }
.title__action-btn ::v-deep .v-btn__content { min-width: 0; max-width: 100%; }
.title__action-label { display: block; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.title__action-btn--split { border-top-right-radius: 0; border-bottom-right-radius: 0; }
.title__action-toggle { flex: 0 0 36px; min-width: 36px !important; width: 36px; padding: 0 !important; border-left: 1px solid rgba(255,255,255,.35); border-top-left-radius: 0; border-bottom-left-radius: 0; }
.title__action-toggle--only { flex: 1 1 auto; width: 100%; padding: 0 16px !important; border-radius: 4px; border: 0; }
@container page-title (max-width: 599px) {
    .title__lead, .title__actions-bar { flex-basis: 100% !important; width: 100%; }
    .title__action-label { white-space: normal; overflow: visible; }
}
@media (max-width: 599px) {
    .title__wrapper {
        gap: 8px;
        .title__lead, .title__actions-bar { flex-basis: 100% !important; width: 100%; }
    }
    .title__icon { display: none; }
}

.title__wrapper.title__wrapper--page {
    position: sticky;
    top: 0;
    z-index: 5;
    flex-shrink: 0;
    min-height: calc(var(--page-content-start) - var(--page-shell-top) - var(--page-content-gap));
    margin: -24px calc(-1 * var(--page-horizontal-padding, 16px)) calc(var(--page-content-gap) - var(--page-header-parent-gap, 0px)) !important;
    padding: 12px 16px;
    width: calc(100% + 2 * var(--page-horizontal-padding, 16px));
    background: #fff;
    border-bottom: 1px solid transparent;
    transition: border-color 180ms ease, box-shadow 180ms ease;
    &.title__wrapper--scrolled { border-bottom-color: #e5e5e5; box-shadow: 0 2px 4px #00000005; }
}
// Compact typography is present on the first render, before mounted identifies the page header.
.title__wrapper {
    .title__text { transition: none; font-size: 18px; font-weight: 600; letter-spacing: -.2px; color: #303030; }
    .title__icon { transition: none; }
    .title__subtitle { font-size: 12px; }
    .title__actions-bar { min-height: 32px; flex-grow: 0; }
    .title__action-btn, .title__action-toggle { height: 32px !important; font-size: 13px; font-weight: 500; letter-spacing: normal; text-transform: none; }
    .back__button { min-width: 30px !important; width: 30px; height: 30px; margin-right: 10px !important; border: 1px solid #e5e5e5; border-radius: 50%; }
    ::v-deep .v-chip { height: 24px; font-size: 11px; }
}
@media (prefers-reduced-motion: reduce) { .title__wrapper--page { transition: none; } }
</style>

<style lang="scss">
/* Portaled v-menu: width follows labels, not the full-width activator button */
.title__action-menu-content {
    min-width: unset !important;
    width: max-content;
    max-width: calc(100vw - 24px);
    .v-list-item__title, .v-list-item__subtitle { white-space: normal; overflow-wrap: anywhere; }
}
</style>

<style lang="scss">
@media (max-width: 599px) {
 .v-menu__content.title__action-menu-content { left: 16px !important; width: calc(100vw - 32px); max-width: calc(100vw - 32px); }
}
</style>
